import React, { useState, useCallback, useMemo, useEffect } from 'react';
import '../global-sass/index.scss';
import PropTypes from 'prop-types';
import { useSelector } from 'react-redux';
import { useRequestData, usePrefs } from 'wsm-common-data';
import { ErrorBoundary } from 'wsm-error-boundary';
import { isBrowser } from 'ws-scripts/modules/environment';
import logger from 'ws-scripts/modules/logger';
import { setNewRelicCustomAttribute } from '@ddc/new-relic';
import { selectIncentivesSearch } from '../features/selectors';
import groupByVehicle from '../utils/groupByVehicle';
import {
	AccountContext,
	IncentivesContext,
	FiltersContext,
	UIContext
} from '../contexts';
import ViewRouter from './ViewRouter';

const WidgetContent = () => {
	return <ViewRouter />;
};

const ContextProviders = ({ children }) => {
	// SSR Bridge: read hydrated Redux state ONCE on mount
	const ssrState = useSelector(selectIncentivesSearch) || {};

	// === AccountContext ===
	const accountValue = useMemo(
		() => ({
			accountData: ssrState.accountData || null,
			isGroupSite: !!(ssrState.accountData?.descendantsList?.length > 0),
			selectedAccountId: null
		}),
		[ssrState.accountData]
	);

	// === IncentivesContext ===
	// BR-VEH-001: apply minimumModelYearThreshold at the context level so ALL
	// consumers (facets, showcase, deep-linking, cards) operate on year-filtered data.
	const prefs = usePrefs();
	const parsed = parseInt(prefs.minimumModelYearThreshold, 10);
	const minimumModelYearThreshold = Number.isNaN(parsed) ? 2 : parsed;
	const minYear = new Date().getFullYear() - minimumModelYearThreshold;

	const incentivesValue = useMemo(() => {
		const raw = ssrState.incentives || [];
		const allByVehicle = groupByVehicle(raw);
		const byVehicle = Object.fromEntries(
			Object.entries(allByVehicle).filter(([, v]) => {
				const y = parseInt(v.year, 10);
				return y >= minYear;
			})
		);
		return {
			incentives: { raw, byVehicle },
			vehicles: ssrState.vehicles || {},
			showcaseMappings: ssrState.showcaseMappings || null,
			loading: ssrState.loading ?? true,
			errors: ssrState.errors || []
		};
	}, [
		ssrState.incentives,
		ssrState.vehicles,
		ssrState.showcaseMappings,
		ssrState.loading,
		ssrState.errors,
		minYear
	]);

	// === FiltersContext ===
	const [activeFilters, setActiveFilters] = useState({
		offerTypes: [],
		year: [],
		bodyStyle: [],
		make: [],
		model: []
	});
	const [staleFilters, setStaleFilters] = useState({});
	const [alertState, setAlertState] = useState(null);

	const selectFacet = useCallback((facetKey, value) => {
		setActiveFilters((prev) => ({
			...prev,
			[facetKey]: [...(prev[facetKey] || []), value]
		}));
		setStaleFilters({});
		setAlertState(null);
	}, []);

	const deselectFacet = useCallback((facetKey, value) => {
		setActiveFilters((prev) => ({
			...prev,
			[facetKey]: prev[facetKey].filter((v) => v !== value)
		}));
		setStaleFilters({});
		setAlertState(null);
	}, []);

	const clearGroup = useCallback((facetKey) => {
		setActiveFilters((prev) => ({ ...prev, [facetKey]: [] }));
		setStaleFilters({});
		setAlertState(null);
	}, []);

	const clearAll = useCallback(() => {
		setActiveFilters({
			offerTypes: [],
			year: [],
			bodyStyle: [],
			make: [],
			model: []
		});
		setStaleFilters({});
		setAlertState(null);
	}, []);

	const hydrateFromUrl = useCallback(
		(validFilters, invalidFilters, newAlertState) => {
			setActiveFilters((prev) => ({ ...prev, ...validFilters }));
			setStaleFilters(invalidFilters);
			setAlertState(newAlertState);
		},
		[]
	);

	const dismissAlert = useCallback(() => {
		setStaleFilters({});
		setAlertState(null);
	}, []);

	const filtersValue = useMemo(
		() => ({
			activeFilters,
			staleFilters,
			alertState,
			selectFacet,
			deselectFacet,
			clearGroup,
			clearAll,
			hydrateFromUrl,
			dismissAlert
		}),
		[
			activeFilters,
			staleFilters,
			alertState,
			selectFacet,
			deselectFacet,
			clearGroup,
			clearAll,
			hydrateFromUrl,
			dismissAlert
		]
	);

	// === UIContext ===
	const requestData = useRequestData();
	const isMobile = requestData.deviceType?.toLowerCase() === 'mobile';
	const [activeView, setActiveView] = useState('default');
	const [selectedVehicleId, setSelectedVehicleId] = useState(null);
	const [detailViewSelectedOfferTab, setDetailViewSelectedOfferTab] =
		useState(null);
	const [originCategorySlug, setOriginCategorySlug] = useState(null);
	const changeView = useCallback((view, vehicleId = null) => {
		setActiveView(view);
		setSelectedVehicleId(vehicleId);
		if (isBrowser && view === 'detail') {
			window.scrollTo(0, 0);
		}
	}, []);

	const changeOfferTab = useCallback((tab) => {
		setDetailViewSelectedOfferTab(tab);
	}, []);

	// Hydrate view from URL on mount (deep link support for ?gvId=)
	useEffect(() => {
		if (isBrowser) {
			const params = new URLSearchParams(window.location.search);
			const gvId = params.get('gvId');

			if (gvId) {
				changeView('detail', gvId);
			}
		}
	}, [changeView]);

	// Sync view state when browser back/forward buttons are used
	useEffect(() => {
		if (!isBrowser) return undefined;

		const handlePopState = () => {
			const params = new URLSearchParams(window.location.search);
			const gvId = params.get('gvId');

			if (gvId) {
				changeView('detail', gvId);
			} else {
				changeView('default');
			}
		};

		window.addEventListener('popstate', handlePopState);
		return () => window.removeEventListener('popstate', handlePopState);
	}, [changeView]);

	const uiValue = useMemo(
		() => ({
			activeView,
			selectedVehicleId,
			detailViewSelectedOfferTab,
			originCategorySlug,
			isMobile,
			changeView,
			changeOfferTab,
			setOriginCategorySlug
		}),
		[
			activeView,
			selectedVehicleId,
			detailViewSelectedOfferTab,
			originCategorySlug,
			isMobile,
			changeView,
			changeOfferTab,
			setOriginCategorySlug
		]
	);

	return (
		<AccountContext.Provider value={accountValue}>
			<IncentivesContext.Provider value={incentivesValue}>
				<FiltersContext.Provider value={filtersValue}>
					<UIContext.Provider value={uiValue}>
						{children}
					</UIContext.Provider>
				</FiltersContext.Provider>
			</IncentivesContext.Provider>
		</AccountContext.Provider>
	);
};

ContextProviders.propTypes = {
	children: PropTypes.node.isRequired
};

const Widget = () => {
	return (
		<ErrorBoundary
			errorHandler={(error, errorInfo) => {
				setNewRelicCustomAttribute(
					'INCENTIVES_SEARCH_ERROR',
					`incentives-search error.\n${error}`
				);
				logger.error(
					`incentives-search error.\n${error}\n${errorInfo.componentStack}`
				);
			}}
		>
			<ContextProviders>
				<WidgetContent />
			</ContextProviders>
		</ErrorBoundary>
	);
};

export default Widget;
